import { aaveOracles, aaveReserves } from '@1delta/data-sdk'
import { multicallRetryUniversal } from '@1delta/providers'
import { AaveOracleAbi } from '../../abis/oracle/AaveOracle'
import { ChainlinkAggregatorAbi } from './fetchers/compoundV3'

/**
 * Feed timestamps for AAVE-family oracles.
 *
 * `AaveOracle.getAssetPrice()` returns a bare uint — no timestamp — so the
 * only way to tell a live feed from a dead one is to resolve the per-asset
 * source (`getSourceOfAsset`) and read `latestRoundData().updatedAt` on it.
 * That matters because AAVE forks are the dominant (often only) price source
 * on several chains: Avalanche has no Compound V3 deployment at all, so
 * before this probe existed NOTHING on that chain was ever checked for
 * staleness.
 *
 * Two multicalls, both `allowFailure` and both best-effort: a chain where the
 * probe fails just yields no timestamps, never a missing price. Roughly half
 * of AAVE's sources are price-cap adapters that do not implement
 * `latestRoundData` — those legitimately return no timestamp.
 */

/** asset (lowercase) -> feed timestamp info */
export type AssetFeedInfo = {
  source: string
  updatedAt: number
  description?: string
}

/** lender -> asset (lowercase) -> feed info */
export type FeedTimestampMap = {
  [lender: string]: { [asset: string]: AssetFeedInfo }
}

const DESCRIPTION_ABI = [
  {
    inputs: [],
    name: 'description',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

const AGGREGATOR_ABI = [...ChainlinkAggregatorAbi, ...DESCRIPTION_ABI] as const

interface ProbeOptions {
  rpcOverrides?: { [chainId: string]: string[] }
  batchSize?: number
  retries?: number
}

function isFailed(r: any): boolean {
  return r === '0x' || r === undefined || r === null
}

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

/**
 * Resolve `updatedAt` for every AAVE-family (lender, asset) pair on a chain.
 *
 * Returns an empty map — never throws — when the chain has no AAVE-family
 * lender or when the RPC calls fail.
 */
export async function probeAaveFeedTimestamps(
  chainId: string,
  options: ProbeOptions = {},
): Promise<FeedTimestampMap> {
  const { rpcOverrides, batchSize, retries = 2 } = options
  const result: FeedTimestampMap = {}

  const oracles = aaveOracles() ?? {}
  const reserves = aaveReserves() ?? {}

  const jobs: { lender: string; oracle: string; asset: string }[] = []
  for (const [lender, chains] of Object.entries(oracles)) {
    const oracle = chains?.[chainId]
    const assets = reserves[lender]?.[chainId] ?? []
    if (!oracle || assets.length === 0) continue
    for (const asset of assets) jobs.push({ lender, oracle, asset })
  }
  if (jobs.length === 0) return result

  try {
    // Pass 1 — resolve the price source behind each (oracle, asset) pair.
    const sourceResults = await multicallRetryUniversal({
      chain: chainId,
      calls: jobs.map((j) => ({
        address: j.oracle,
        name: 'getSourceOfAsset',
        params: [j.asset],
      })),
      abi: AaveOracleAbi as any,
      batchSize,
      maxRetries: retries,
      providerId: undefined,
      allowFailure: true,
      overrdies: rpcOverrides,
      logErrors: false,
    })

    const sourceOf = new Map<number, string>()
    const uniqueSources: string[] = []
    const sourceIndex = new Map<string, number>()

    jobs.forEach((_, i) => {
      const raw = sourceResults[i]
      if (isFailed(raw)) return
      const source = String(raw).toLowerCase()
      if (source === ZERO_ADDRESS) return
      sourceOf.set(i, source)
      if (!sourceIndex.has(source)) {
        sourceIndex.set(source, uniqueSources.length)
        uniqueSources.push(source)
      }
    })

    if (uniqueSources.length === 0) return result

    // Pass 2 — read the round data (and label) of each distinct source once.
    const roundResults = await multicallRetryUniversal({
      chain: chainId,
      calls: uniqueSources.flatMap((source) => [
        { address: source, name: 'latestRoundData', params: [] },
        { address: source, name: 'description', params: [] },
      ]),
      abi: AGGREGATOR_ABI as any,
      batchSize,
      maxRetries: retries,
      providerId: undefined,
      allowFailure: true,
      overrdies: rpcOverrides,
      logErrors: false,
    })

    const infoOfSource = new Map<string, AssetFeedInfo>()
    uniqueSources.forEach((source, i) => {
      const round = roundResults[i * 2]
      const description = roundResults[i * 2 + 1]
      // Price-cap adapters and other non-Chainlink sources have no round data
      if (isFailed(round) || !Array.isArray(round)) return
      const updatedAt = Number(round[3])
      if (!(updatedAt > 0)) return
      infoOfSource.set(source, {
        source,
        updatedAt,
        description: isFailed(description) ? undefined : String(description),
      })
    })

    for (const [i, source] of sourceOf) {
      const info = infoOfSource.get(source)
      if (!info) continue
      const { lender, asset } = jobs[i]
      if (!result[lender]) result[lender] = {}
      result[lender][asset.toLowerCase()] = info
    }
  } catch {
    // Best effort — a failed probe simply means no timestamps this cycle
    return result
  }

  return result
}
